<template>
  <div>
    <WarehousePurchaseReceiptDialog
      v-model="dialogStatus"
      :editedItem="editedItem"
      @success="onSuccess"
    />
    <warehouse-purchase-order-dialog
      v-model="purchaseOrderDialog"
      :editedItem="editedItem"
      :is-purchase-order="purchaseOrder"
      @success="onSuccess"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="group.warehouseName"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['supplier']"
        >
          <template v-slot:item.document_type="{ item }">
            <span>{{ documentTypes[item.document_type] }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="getStatusColor(item.status)">
              {{ $t(statusKeyMap[item.status]) }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                class="mr-2"
                icon="mdi-file-document-arrow-right-outline"
                :tooltip="$t('warehouse.supplier_delivered')"
                @click="
                  editedItem = item;
                  dialogStatus = true;
                "
              />
              <circle-button
                icon="mdi-eye"
                :tooltip="$t('overview')"
                class="mr-2"
                @click="editItem(item)"
              />
              <remove-button @click="deleteItem(item)" />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-cart-arrow-down"
        :title="$t('no_purchases')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_purchase') + ': ' + lastEntryDate
            : $t('no_purchases_yet')
        "
      >
        <warehouse-button-order @order="orderGroceries($event)" />
      </empty-state>

      <div class="fab-container">
        <v-btn color="primary" class="mb-2" block @click="orderGroceries(true)">
          <v-icon left>mdi-cart-arrow-down</v-icon>
          {{ $t("warehouse.purchase_order") }}
        </v-btn>
        <v-btn color="info" block @click="orderGroceries(false)">
          <v-icon left>mdi-file-document-outline</v-icon>
          {{ $t("warehouse.request_quote") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate View -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="activeAggregatedItems.length > 0 || loading"
          :headers="aggregateHeaders"
          :items="activeAggregatedItems"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
                {{ $t("warehouse.last_purchase") }}:
                <strong>{{ lastEntryDate }}</strong>
              </span>
              <v-spacer />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
              <warehouse-button-order @order="orderGroceries($event)" />
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) || "-" }}
          </template>
          <template v-slot:item.total_quantity="{ item }">
            {{ formatNumber(item.total_quantity) }}
          </template>
          <template v-slot:item.total_amount="{ item }">
            {{ formatNumber(item.total_amount) || "" }}
          </template>
          <template v-slot:item.avg_unit_price="{ item }">
            {{ formatNumber(item.avg_unit_price) || "" }}
          </template>
        </v-data-table>
        <empty-state
          v-if="!loading && activeAggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
          :subtitle="$t('warehouse.no_entries_for_period')"
        />
      </template>

      <!-- Regular Document View -->
      <v-data-table
        v-else-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_purchase") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
            <v-spacer />
            <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
              <v-btn :value="true" small>
                <v-icon left small>mdi-sigma</v-icon>
                {{ $t("warehouse.aggregate") }}
              </v-btn>
            </v-btn-toggle>
            <warehouse-button-order @order="orderGroceries($event)" />
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              class="mr-2"
              icon="mdi-file-document-arrow-right-outline"
              :tooltip="$t('warehouse.supplier_delivered')"
              @click="
                editedItem = item;
                dialogStatus = true;
              "
            />
            <circle-button
              icon="mdi-eye"
              :tooltip="$t('overview')"
              class="mr-2"
              color="primary"
              @click="editItem(item)"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <autocomplete
            hide-details
            color="white"
            v-model="item.status"
            :items="statuses"
            item-text="name"
            item-value="value"
            @change="updateStatus(item)"
          />
        </template>
        <template v-slot:item.document_type="{ item }">
          <span>{{ documentTypes[item.document_type] }}</span>
        </template>
      </v-data-table>

      <empty-state
        v-if="!aggregateMode && !loading && filteredDocuments.length === 0"
        icon="mdi-cart-arrow-down"
        :title="$t('no_purchases')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_purchase') + ': ' + lastEntryDate
            : $t('no_purchases_yet')
        "
        @click:subtitle="setFiltersToLastEntry()"
      >
        <warehouse-button-order @order="orderGroceries($event)" />
      </empty-state>
    </template>

    <!-- Removed local v-snackbar -->
    <global-confirm-box ref="confirmBox" />
  </div>
</template>

<script>
import WarehousePurchaseReceiptDialog from "@/components/warehouse/dialogs/WarehousePurchaseReceiptDialog.vue";
import WarehousePurchaseOrderDialog from "@/components/warehouse/dialogs/WarehousePurchaseOrderDialog.vue";
import { WarehouseButtonOrder } from "@/components/warehouse/buttons";
import { orderDocumentGrocery, requestOrderDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import warehouseService from "@/services/warehouse";
import { isInDateRange } from "@/utils/warehouseUtils";

export default {
  components: {
    WarehousePurchaseReceiptDialog,
    WarehousePurchaseOrderDialog,
    WarehouseButtonOrder,
    EmptyState,
    MobileDataCards,
  },
  mixins: [warehouseDocumentTabMixin, warehouseFiltersMixin],
  data() {
    return {
      dialogStatus: false,
      purchaseOrder: false,
      purchaseOrderDialog: false,
      editedItem: JSON.parse(JSON.stringify(orderDocumentGrocery)),
    };
  },
  created() {
    this.load();
    this.$store.dispatch("warehouse/fetchSuppliers").catch(() => {
      this.showErrorSnackbar(this.$t("error_loading_suppliers"));
    });
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [orderRes, requestRes] = await Promise.all([
          warehouseService.getWarehouseDocuments({ document_type: "order" }),
          warehouseService.getWarehouseDocuments({ document_type: "request" }),
        ]);
        this.documents = orderRes.data
          .concat(requestRes.data)
          .map((item) => ({
            ...item,
            groceries: item.groceries.map((g) => ({ ...g, order: g.quantity })),
          }))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } catch (error) {
        console.error("Error loading documents:", error);
        this.showErrorSnackbar(this.$t("error_loading_purchase_documents"));
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },
    async updateStatus(item) {
      try {
        await this.handleRequest(
          warehouseService.patchWarehouse(item.id, { data: { status: item.status } }),
          {
            successMessage: this.$t("purchase_status_updated"),
            errorMessagePrefix: this.$t("error_updating_purchase_status") + ": ",
          }
        );
      } catch (error) {
        this.load();
        console.error("Failed to update status", error);
      }
    },
    editItem(item) {
      this.editedItem = JSON.parse(JSON.stringify(item));
      this.purchaseOrderDialog = true;
    },
    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_document"),
          this.$t("warehouse.confirm_delete_document"),
          { color: "error" }
        )
        .catch(() => false);
      if (!confirmed) return;

      try {
        await this.handleRequest(warehouseService.removeWarehouse(item.id), {
          successMessage: this.$t("purchase_document_deleted"),
          errorMessagePrefix: this.$t("error_deleting_purchase_document") + ": ",
        });
        this.documents = this.documents.filter((d) => d.id !== item.id);
      } catch (error) {
        console.error("Failed to delete item", error);
      }
    },
    orderGroceries(isPurchaseOrder) {
      this.purchaseOrder = isPurchaseOrder;
      const model = isPurchaseOrder ? orderDocumentGrocery : requestOrderDocumentGrocery;
      this.editedItem = JSON.parse(JSON.stringify(model));
      this.purchaseOrderDialog = true;
    },
    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
  },
  computed: {
    filteredDocuments() {
      const { search, dates, status, documentType, warehouse, suppliers } = this.filters;
      const enriched = this.documents.map((doc) => ({
        ...doc,
        statusFilter: this.translate(
          this.statuses.find((s) => s.value === doc.status)?.name
        ),
        typeFilter: this.translate(this.documentTypes[doc.document_type]),
        supplier:
          this.$store.state.warehouse.suppliers.find((s) => s.id === doc.supplier_id)
            ?.name || this.$t("warehouse.unknown_supplier"),
      }));

      return this.customSearch(
        enriched,
        [
          ...this.headers,
          { text: this.$t("warehouse.document_type"), value: "typeFilter" },
          { text: this.$t("status"), value: "statusFilter" },
        ],
        search
      ).filter(
        (item) =>
          isInDateRange(item, "document_date", dates.from, dates.to) &&
          (!status || item.status === status) &&
          (!documentType || item.document_type === documentType) &&
          warehouse.some((w) => w.id === item.warehouse_id) &&
          suppliers.some((s) => s.id === item.supplier_id)
      );
    },
    headers() {
      const multiWarehouse = this.filters.warehouse.length > 1;

      return [
        { text: "ID", align: "start", value: "id", mobile: false, width: "70px" },
        {
          text: this.$t("stock"),
          value: "warehouse.name",
          mobile: false,
          show: multiWarehouse,
        },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 4 },
        { text: this.$t("warehouse.document_type"), value: "document_type", cols: 4 },
        { text: this.$t("status"), value: "status", cols: 4 },
        { text: this.$t("date"), value: "document_date", cols: 4 },
        { text: this.$t("supplier"), value: "supplier", cols: 4 },
        {
          text: this.$t("warehouse.offers_deadline"),
          value: "offer_deadline",
          mobile: false,
        },
        {
          text: this.$t("warehouse.delivery_deadline"),
          value: "delivery_deadline",
          mobile: false,
        },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 4,
          width: "150px",
          align: "right",
        },
      ].filter((h) => h.show !== false);
    },
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        request: this.$t("warehouse.request_quote"),
        entry: this.$t("warehouse.entry"),
      };
    },
    statuses() {
      return Object.entries(this.statusKeyMap).map(([value, key]) => ({
        name: this.$t(key),
        value,
      }));
    },
    aggregateHeaders() {
      return [
        { text: this.$t("code"), value: "grocery_code", width: "100px" },
        { text: this.$t("warehouse.product"), value: "grocery_name", width: "350px" },
        {
          text: this.$t("unit_of_measurment"),
          value: "measure",
          width: "90px",
          align: "center",
        },
        {
          text: this.$t("warehouse.total_quantity"),
          value: "total_quantity",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.avg_unit_price"),
          value: "avg_unit_price",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.total_amount"),
          value: "total_amount",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.document_count"),
          value: "document_count",
          align: "center",
          width: "80px",
        },
      ];
    },
    exportItems() {
      if (this.aggregateMode) {
        return this.activeAggregatedItems.map((item) => ({
          ...item,
          total_quantity: this.formatNumber(item.total_quantity),
          avg_unit_price: this.formatNumber(item.avg_unit_price),
          total_amount: this.formatNumber(item.total_amount),
        }));
      }
      return this.filteredDocuments.map((item) => ({
        ...item,
        status: this.translate(this.statuses.find((s) => s.value === item.status)?.name),
        document_type: this.translate(this.documentTypes[item.document_type]),
      }));
    },
    exportTitle() {
      const parts = [
        this.filters.warehouse.map((w) => w.name).join(", ") || this.$t("stock"),
        this.$t("warehouse.ordered").toLowerCase(),
      ];
      if (this.aggregateMode) parts.push(this.$t("warehouse.aggregate"));
      return parts.join(" ");
    },
  },
  watch: {
    purchaseOrderDialog(val) {
      if (!val) {
        this.editedItem = JSON.parse(JSON.stringify(orderDocumentGrocery));
      }
    },
  },
};
</script>

<style scoped>
.fab-container {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
}
</style>
